import { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Edit2, X, Eye, EyeOff, Bug } from 'lucide-react';
import { supabase } from '@/lib/supabase';

interface Package {
  id: number;
  name: string;
  price: string;
  includes: string[];
  description?: string;
  image_url?: string;
  duration?: string;
  is_featured?: boolean;
  is_active?: boolean;
  sort_order?: number;
}

export function PackageManager() {
  const queryClient = useQueryClient();
  const [newPackage, setNewPackage] = useState({
    name: '',
    price: '',
    includes: '',
    description: '',
    imageUrl: '',
    duration: '',
  });
  const [uploading, setUploading] = useState(false);
  const [editingPackage, setEditingPackage] = useState<Package | null>(null);
  const [deletingPackage, setDeletingPackage] = useState<number | null>(null);
  const [togglingPackage, setTogglingPackage] = useState<number | null>(null);
  
  // Form for editing a package with initial empty state
  const [editForm, setEditForm] = useState({
    name: '',
    price: '',
    includes: '',
    description: '',
    imageUrl: '',
    duration: '',
  });

  // Debug function to test database connection and table structure
  const debugDatabase = async () => {
    try {
      console.log('=== Database Debug ===');
      
      // Test 1: Check if we can query the table
      const { data: testData, error: testError } = await supabase
        .from('sns_packages')
        .select('*')
        .limit(1);
      
      console.log('Test 1 - Query result:', { data: testData, error: testError });
      
      // Test 2: Check table structure
      let structureData = null;
      let structureError = null;
      try {
        const result = await supabase
          .rpc('get_table_structure', { table_name: 'sns_packages' });
        structureData = result.data;
        structureError = result.error;
      } catch (err) {
        structureError = 'RPC function not available';
      }
      
      console.log('Test 2 - Table structure:', { data: structureData, error: structureError });
      
      // Test 3: Check current user
      const { data: { user } } = await supabase.auth.getUser();
      console.log('Test 3 - Current user:', user);
      
      // Test 4: Check admin status
      if (user) {
        const { data: adminData, error: adminError } = await supabase
          .from('admin_users')
          .select('user_id')
          .eq('user_id', user.id)
          .maybeSingle();
        
        console.log('Test 4 - Admin status:', { data: adminData, error: adminError });
      }
      
      toast.success('Debug info logged to console');
    } catch (error) {
      console.error('Debug error:', error);
      toast.error('Debug failed');
    }
  };

  const { data: packages = [], isLoading, error } = useQuery({
    queryKey: ['packages'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('sns_packages')
        .select('*')
        .order('id', { ascending: true });

      if (error) {
        console.error('Error fetching packages:', error);
        throw error;
      }
      return data || [];
    },
  });

  // Log any query errors
  if (error) {
    console.error('Query error:', error);
  }

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>, isEditing = false) => {
    try {
      const file = event.target.files?.[0];
      if (!file) return;

      setUploading(true);
      console.log('Starting file upload:', { fileName: file.name, fileSize: file.size });

      // Validate file type
      if (!file.type.startsWith('image/')) {
        throw new Error('Please upload an image file');
      }

      // Validate file size (5MB limit)
      if (file.size > 5 * 1024 * 1024) {
        throw new Error('File size must be less than 5MB');
      }

      const fileExt = file.name.split('.').pop()?.toLowerCase() || '';
      const fileName = `${Date.now()}.${fileExt}`;
      
      const { error: uploadError, data } = await supabase.storage
        .from('package-images')
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: false,
          contentType: file.type
        });

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('package-images')
        .getPublicUrl(fileName);

      if (isEditing) {
        setEditForm(prev => ({ ...prev, imageUrl: publicUrl }));
      } else {
        setNewPackage(prev => ({ ...prev, imageUrl: publicUrl }));
      }
      
      toast.success('Image uploaded successfully');
    } catch (error: any) {
      console.error('Upload error:', error);
      toast.error(error.message || 'Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  // Start editing a package
  const startEditing = (pkg: Package) => {
    setEditingPackage(pkg);
    setEditForm({
      name: pkg.name,
      price: pkg.price,
      includes: Array.isArray(pkg.includes) ? pkg.includes.join('\n') : '',
      description: pkg.description || '',
      imageUrl: pkg.image_url || '',
      duration: pkg.duration || '',
    });
  };

  // Cancel editing
  const cancelEditing = () => {
    setEditingPackage(null);
    setEditForm({
      name: '',
      price: '',
      includes: '',
      description: '',
      imageUrl: '',
      duration: '',
    });
  };
  
  // Toggle package visibility
  const togglePackageVisibility = async (id: number, currentStatus: boolean) => {
    if (togglingPackage === id) return; // Prevent multiple clicks
    
    setTogglingPackage(id);
    try {
      console.log('=== Toggle Package Visibility ===');
      console.log('Package ID:', id);
      console.log('Current Status:', currentStatus);
      console.log('New Status:', !currentStatus);
      
      // Check current user
      const { data: { user } } = await supabase.auth.getUser();
      console.log('Current user:', user);
      
      if (!user) {
        toast.error('User not authenticated');
        return;
      }
      
      // Check admin status
      const { data: adminData, error: adminError } = await supabase
        .from('admin_users')
        .select('user_id')
        .eq('user_id', user.id)
        .maybeSingle();
      
      console.log('Admin check:', { data: adminData, error: adminError });
      
      if (adminError || !adminData) {
        toast.error('User is not an admin');
        return;
      }
      
      const { error } = await supabase
        .from('sns_packages')
        .update({ is_active: !currentStatus })
        .eq('id', id);

      if (error) {
        console.error('Error updating package visibility:', error);
        toast.error(`Error: ${error.message}`);
        return;
      }

      console.log('Package visibility updated successfully');
      toast.success(`Package ${currentStatus ? 'hidden' : 'shown'} successfully`);
      queryClient.invalidateQueries({ queryKey: ['packages'] });
    } catch (error: any) {
      console.error('Error updating package visibility:', error);
      toast.error(error.message || 'Error updating package visibility');
    } finally {
      setTogglingPackage(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const { error } = await supabase
        .from('sns_packages')
        .insert([{
          name: newPackage.name,
          price: newPackage.price,
          includes: newPackage.includes.split('\n').filter(Boolean),
          description: newPackage.description,
          image_url: newPackage.imageUrl,
          duration: newPackage.duration,
          is_active: true
        }]);

      if (error) {
        console.error('Error creating package:', error);
        throw error;
      }

      toast.success('Package created successfully');
      setNewPackage({
        name: '',
        price: '',
        includes: '',
        description: '',
        imageUrl: '',
        duration: ''
      });
      queryClient.invalidateQueries({ queryKey: ['packages'] });
    } catch (error: any) {
      console.error('Error creating package:', error);
      toast.error(error.message || 'Error creating package');
    }
  };

  const handleDelete = async (id: number) => {
    if (deletingPackage === id) return; // Prevent multiple clicks
    
    if (!window.confirm('Are you sure you want to delete this package?')) {
      return;
    }
    
    setDeletingPackage(id);
    try {
      console.log('=== Delete Package ===');
      console.log('Package ID:', id);
      
      // Check current user
      const { data: { user } } = await supabase.auth.getUser();
      console.log('Current user:', user);
      
      if (!user) {
        toast.error('User not authenticated');
        return;
      }
      
      // Check admin status
      const { data: adminData, error: adminError } = await supabase
        .from('admin_users')
        .select('user_id')
        .eq('user_id', user.id)
        .maybeSingle();
      
      console.log('Admin check:', { data: adminData, error: adminError });
      
      if (adminError || !adminData) {
        toast.error('User is not an admin');
        return;
      }
      
      const { error } = await supabase
        .from('sns_packages')
        .delete()
        .eq('id', id);

      if (error) {
        console.error('Error deleting package:', error);
        toast.error(`Error: ${error.message}`);
        return;
      }

      console.log('Package deleted successfully');
      toast.success('Package deleted successfully');
      queryClient.invalidateQueries({ queryKey: ['packages'] });
    } catch (error: any) {
      console.error('Error deleting package:', error);
      toast.error(error.message || 'Error deleting package');
    } finally {
      setDeletingPackage(null);
    }
  };
  
  // Save edited package
  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingPackage) return;
    
    try {
      console.log('Updating package:', editingPackage.id, editForm);
      
      const { error } = await supabase
        .from('sns_packages')
        .update({
          name: editForm.name,
          price: editForm.price,
          includes: editForm.includes.split('\n').filter(Boolean),
          description: editForm.description,
          image_url: editForm.imageUrl,
          duration: editForm.duration,
        })
        .eq('id', editingPackage.id);

      if (error) {
        console.error('Error updating package:', error);
        throw error;
      }

      toast.success('Package updated successfully');
      cancelEditing();
      queryClient.invalidateQueries({ queryKey: ['packages'] });
    } catch (error: any) {
      console.error('Error updating package:', error);
      toast.error(error.message || 'Error updating package');
    }
  };

  if (isLoading) {
    return <div className="flex justify-center items-center p-8">Loading packages...</div>;
  }

  if (error) {
    return <div className="flex justify-center items-center p-8 text-red-500">Error loading packages: {error.message}</div>;
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold">Package Management</h2>
        <Button 
          variant="outline" 
          onClick={debugDatabase}
          className="flex items-center gap-1"
        >
          <Bug className="w-4 h-4" /> Debug
        </Button>
      </div>
      
      <div className="mb-8">
        <h3 className="text-xl font-semibold mb-4">Add New Package</h3>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Package Name</label>
            <Input
              value={newPackage.name}
              onChange={(e) => setNewPackage({ ...newPackage, name: e.target.value })}
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Price</label>
            <Input
              value={newPackage.price}
              onChange={(e) => setNewPackage({ ...newPackage, price: e.target.value })}
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Duration</label>
            <Input
              value={newPackage.duration}
              onChange={(e) => setNewPackage({ ...newPackage, duration: e.target.value })}
              placeholder="e.g., 3 days, 1 week"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Description</label>
            <Textarea
              value={newPackage.description}
              onChange={(e) => setNewPackage({ ...newPackage, description: e.target.value })}
              rows={3}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Includes (one per line)</label>
            <Textarea
              value={newPackage.includes}
              onChange={(e) => setNewPackage({ ...newPackage, includes: e.target.value })}
              required
              rows={5}
              placeholder="Enter each included item on a new line"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Package Image</label>
            <Input
              type="file"
              accept="image/*"
              onChange={(e) => handleImageUpload(e, false)}
              disabled={uploading}
            />
            {newPackage.imageUrl && (
              <img
                src={newPackage.imageUrl}
                alt="Package preview"
                className="mt-2 w-32 h-32 object-cover rounded"
              />
            )}
          </div>
          <Button type="submit" disabled={uploading}>Add Package</Button>
        </form>
      </div>

      <div>
        <h3 className="text-xl font-semibold mb-4">Existing Packages</h3>
        <div className="space-y-4">
          {Array.isArray(packages) && packages.map((pkg) => (
            <div key={pkg.id} className="p-4 border rounded-lg">
              {editingPackage?.id === pkg.id ? (
                <div className="p-4 border rounded-lg border-primary">
                  <div className="flex justify-between items-center mb-4">
                    <h4 className="text-lg font-medium">Edit Package</h4>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={cancelEditing}
                      className="flex items-center gap-1"
                    >
                      <X className="w-4 h-4" /> Cancel
                    </Button>
                  </div>
                  <form onSubmit={handleUpdate} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">Package Name</label>
                      <Input
                        value={editForm.name}
                        onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Price</label>
                      <Input
                        value={editForm.price}
                        onChange={(e) => setEditForm({ ...editForm, price: e.target.value })}
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Duration</label>
                      <Input
                        value={editForm.duration}
                        onChange={(e) => setEditForm({ ...editForm, duration: e.target.value })}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Description</label>
                      <Textarea
                        value={editForm.description}
                        onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                        rows={3}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Includes (one per line)</label>
                      <Textarea
                        value={editForm.includes}
                        onChange={(e) => setEditForm({ ...editForm, includes: e.target.value })}
                        required
                        rows={5}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Package Image</label>
                      <Input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleImageUpload(e, true)}
                        disabled={uploading}
                      />
                      {editForm.imageUrl && (
                        <img
                          src={editForm.imageUrl}
                          alt="Package preview"
                          className="mt-2 w-32 h-32 object-cover rounded"
                        />
                      )}
                    </div>
                    <Button type="submit" disabled={uploading}>Save Changes</Button>
                  </form>
                </div>
              ) : (
                <div className={`flex justify-between items-start ${!pkg.is_active ? 'opacity-60' : ''}`}>
                  <div>
                    <div className="flex items-center gap-2">
                      <h4 className="text-lg font-medium">{pkg.name}</h4>
                      {!pkg.is_active && (
                        <span className="text-xs px-2 py-0.5 bg-gray-200 text-gray-700 rounded-full">Hidden</span>
                      )}
                    </div>
                    <p className="text-gray-600">${pkg.price} {pkg.duration ? `/ ${pkg.duration}` : ''}</p>
                    {pkg.description && <p className="text-gray-600 mt-2">{pkg.description}</p>}
                    {pkg.image_url && (
                      <img
                        src={pkg.image_url}
                        alt={pkg.name}
                        className="mt-2 w-32 h-32 object-cover rounded"
                      />
                    )}
                    <ul className="list-disc list-inside mt-2">
                      {(pkg.includes || []).map((item, index) => (
                        <li key={index}>{item}</li>
                      ))}
                    </ul>
                  </div>
                  <div className="flex gap-2">
                    <Button 
                      variant="outline" 
                      onClick={() => startEditing(pkg)}
                      className="flex items-center gap-1"
                    >
                      <Edit2 className="w-4 h-4" /> Edit
                    </Button>
                    <Button
                      variant={pkg.is_active ? "outline" : "secondary"}
                      onClick={() => togglePackageVisibility(pkg.id, !!pkg.is_active)}
                      className="flex items-center gap-1"
                      title={pkg.is_active ? "Hide package" : "Show package"}
                      disabled={togglingPackage === pkg.id}
                    >
                      {pkg.is_active ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      {togglingPackage === pkg.id ? 'Updating...' : (pkg.is_active ? "Hide" : "Show")}
                    </Button>
                    <Button
                      variant="destructive"
                      onClick={() => handleDelete(pkg.id)}
                      disabled={deletingPackage === pkg.id}
                    >
                      {deletingPackage === pkg.id ? 'Deleting...' : 'Delete'}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}